import { Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type {
  KbPermission,
  KnowledgeBase,
} from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  useGrantKbPermission,
  useKbPermissions,
  useRevokeKbPermission,
} from "@/features/knowledge-bases/hooks/useKbPermissions";
import { useTeams } from "@/features/teams/hooks/useTeams";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

interface Props {
  kb: KnowledgeBase;
}

const PERMISSIONS: KbPermission[] = ["read", "write", "admin"];
const PERMISSION_LABELS: Record<KbPermission, string> = {
  read: "Leitura",
  write: "Escrita",
  admin: "Administrador",
};

export function PermissionsTab({ kb }: Props) {
  const { data: permissions, isLoading } = useKbPermissions(kb.id);
  const { data: teams } = useTeams({ include_inactive: false });
  const grant = useGrantKbPermission(kb.id);
  const revoke = useRevokeKbPermission(kb.id);

  const canManage = useCan(Action.KNOWLEDGE_BASE_PERMISSION_GRANTED, {
    owner_team_id: kb.owner_team_id,
  });

  const ownerTeam = teams?.find((t) => t.id === kb.owner_team_id);

  const grantedTeamIds = new Set([
    kb.owner_team_id,
    ...(permissions?.map((p) => p.team_id) ?? []),
  ]);
  const availableTeams =
    teams?.filter((t) => !grantedTeamIds.has(t.id)) ?? [];

  const [pickedTeam, setPickedTeam] = useState("");
  const [pickedPermission, setPickedPermission] =
    useState<KbPermission>("read");

  async function handleGrant() {
    if (!pickedTeam) return;
    try {
      await grant.mutateAsync({
        team_id: pickedTeam,
        permission: pickedPermission,
      });
      toast.success("Permissão concedida");
      setPickedTeam("");
      setPickedPermission("read");
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleRevoke(teamId: string) {
    try {
      await revoke.mutateAsync(teamId);
      toast.success("Permissão revogada");
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <div className="space-y-4 py-4">
      <div className="rounded-md border p-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="font-medium">{ownerTeam?.name ?? "Owner"}</span>
          <Badge variant="outline">Owner (leitura implícita)</Badge>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando...</p>
      ) : null}

      {permissions && permissions.length > 0 ? (
        <ul className="divide-y rounded-md border">
          {permissions.map((p) => (
            <li
              key={p.team_id}
              className="flex items-center justify-between px-3 py-2"
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium">{p.team_name}</span>
                <span className="text-xs text-muted-foreground">
                  {PERMISSION_LABELS[p.permission]}
                </span>
              </div>
              {canManage ? (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleRevoke(p.team_id)}
                  disabled={revoke.isPending}
                  aria-label="Revogar permissão"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : !isLoading ? (
        <p className="text-sm text-muted-foreground">
          Nenhuma permissão adicional.
        </p>
      ) : null}

      {canManage ? (
        <div className="space-y-2 border-t pt-4">
          <h3 className="text-sm font-semibold">Conceder permissão</h3>
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="kb-pick-team">Time</Label>
              <select
                id="kb-pick-team"
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                value={pickedTeam}
                onChange={(e) => setPickedTeam(e.target.value)}
              >
                <option value="">Selecione</option>
                {availableTeams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="kb-pick-perm">Permissão</Label>
              <select
                id="kb-pick-perm"
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                value={pickedPermission}
                onChange={(e) =>
                  setPickedPermission(e.target.value as KbPermission)
                }
              >
                {PERMISSIONS.map((p) => (
                  <option key={p} value={p}>
                    {PERMISSION_LABELS[p]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleGrant}
                disabled={!pickedTeam || grant.isPending}
              >
                Conceder
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
